import { memo, useCallback, useMemo } from 'react';

import { type BaseCustomField } from '@air/api/types';
import { useBatchUpdateFormAssetsMutation } from '@air/feature-content-collection/hooks';
import {
  type ContentCollectionCustomField,
  contentCollectionSessionIdSelector,
} from '@air/feature-content-collection/redux';

import { ContentCollectionBaseCustomFieldDateInput } from '~/components/ContentCollection/ContentCollectionBaseCustomFieldDateInput';
import { type ContentCollectionBaseCustomFieldsProps } from '~/components/ContentCollection/ContentCollectionBaseCustomFieldsSelect';
import { useAirStore } from '~/utils/ReduxUtils';

export type ContentCollectionCustomFieldDateInputProps = {
  customField: BaseCustomField;
  onSuccess?: (params: { customFieldWithValues: ContentCollectionCustomField; date: string | undefined }) => void;
  value: string | undefined;
} & Omit<ContentCollectionBaseCustomFieldsProps, 'field'>;

export const ContentCollectionCustomFieldDateInput = memo(
  ({ clips, customField, disabled, onSuccess, value, showDescription }: ContentCollectionCustomFieldDateInputProps) => {
    const store = useAirStore();
    const { mutate: batchUpdateFormAssets, isPending } = useBatchUpdateFormAssetsMutation();

    const customFieldWithValues = useMemo(
      () => ({
        ...customField,
        value: null,
        values: null,
        dateValue: value,
      }),
      [customField, value],
    );

    const onDateSelection = useCallback(
      (date: string | undefined) => {
        const sessionId = contentCollectionSessionIdSelector(store.getState());

        if (!sessionId) {
          throw new Error('No session id');
        }

        batchUpdateFormAssets(
          {
            assetIds: clips.map(({ assetId }) => assetId),
            formSessionId: sessionId,
            update: {
              customFields: [{ customFieldId: customField.id, dateValue: date }],
            },
          },
          {
            onSuccess: () => {
              onSuccess?.({ customFieldWithValues, date });
            },
          },
        );
      },
      [clips, batchUpdateFormAssets, customField.id, customFieldWithValues, onSuccess, store],
    );

    return (
      <ContentCollectionBaseCustomFieldDateInput
        disabled={disabled || isPending}
        clips={clips}
        field={customFieldWithValues}
        onDateSelection={onDateSelection}
        showDescription={showDescription}
      />
    );
  },
);

ContentCollectionCustomFieldDateInput.displayName = 'ContentCollectionCustomFieldDateInput';
